// ============================================================================
// SECTION 1: CONSTANTS & CONFIGURATION
// ============================================================================

// Shopify API Configuration
const STOREFRONT_ACCESS_TOKEN = "f8bbed536ea34b67bdc95f2578b9c6a5";
const GRAPHQL_URL = `/api/2025-01/graphql.json`;

/**
 * Step definitions for the configurator flow.
 * Each step represents a category of products the user can select from.
 */
const STEP_DEFINITIONS = [
  {
    key: "PC",
    name: "PC",
    description: "The brain of the operation",
    dataKey: "pc",
    allowMultiple: false,
  },
  {
    key: "CHASSIS",
    name: "Chassis",
    description: "Start your foundation",
    dataKey: "chassis",
    allowMultiple: false,
  },
  {
    key: "MONITOR_SETUP",
    name: "Stands",
    description: "Monitor mounting",
    dataKey: "monitor_setup",
    allowMultiple: false,
  },
  {
    key: "MONITOR",
    name: "Monitor",
    description: "Visual immersion",
    dataKey: "monitor",
    allowMultiple: false,
  },
  {
    key: "SEATS",
    name: "Seats",
    description: "Comfort & Support",
    dataKey: "seats",
    allowMultiple: true,
  },
  {
    key: "DIRECT_DRIVE",
    name: "Direct Drive",
    description: "Force Feedback Base",
    dataKey: "direct_drive",
    allowMultiple: false,
  },
  {
    key: "WHEEL",
    name: "Steering",
    description: "Control interface",
    dataKey: "steering_wheel",
    allowMultiple: true,
  },
  {
    key: "PEDALS",
    name: "Pedals",
    description: "Brake, Throttle, Clutch",
    dataKey: "pedal_set",
    allowMultiple: false,
  },
  {
    key: "SHIFTER",
    name: "Shifter",
    description: "Gear selection",
    dataKey: "shifter",
    allowMultiple: false,
  },
  {
    key: "HANDBRAKE",
    name: "Handbrake",
    description: "Slide control",
    dataKey: "handbrake",
    allowMultiple: false,
  },
];

// Swipe/scroll observers must ignore scrolls the app itself triggers;
// locks release after the scroll animation settles
const SELECTION_SCROLL_LOCK_MS = 800;
const NAV_SCROLL_LOCK_MS = 1000;

// sessionStorage key for the desktop build-drawer open/closed state
const DRAWER_STATE_KEY = "rf_drawer_open";

// Product data loaded from configurator-data.js (EVO_OVERRIDES, ALPHA_OVERRIDES, configuratorData)

// Application State
let partsData = { steps: [] };

// Product cache — keyed by variant/product ID to avoid redundant fetches
const productCache = {};

/**
 * Global application state object.
 * Tracks current progress through the configurator flow.
 */
const appState = {
  currentStep: 0,
  selections: {}, // Map of step index to selected product
  skippedSteps: {}, // Map of step index to boolean (skipped)
  furthestStep: 0,
  totalSteps: 0,
  isReviewStep: false,
  programmaticScrollCount: 0, // Counter to prevent infinite loops in mobile observer
};

// UI Element References
let rfElements = {};

// Mobile navigation observer
let navObserver = null;

// Module-level navigation context (updated by updateNavigationButtons, read by handlers)
let navContext = {};

// ============================================================================
// SECTION 2: UTILITY FUNCTIONS
// ============================================================================

/**
 * Sanitizes HTML string to prevent XSS attacks.
 * @param {string} str - String to escape
 * @returns {string} Escaped HTML string
 */
function escapeHTML(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/**
 * Sanitizes URLs to prevent XSS and ensure safe protocols.
 * @param {string} url - URL to sanitize
 * @returns {string} Sanitized URL or empty string if invalid
 */
function escapeURL(url) {
  if (!url) return "";
  try {
    // Parse URL to validate format
    const parsed = new URL(url, window.location.origin);
    // Only allow http/https protocols
    if (!["http:", "https:"].includes(parsed.protocol)) {
      console.warn(`Blocked unsafe URL protocol: ${parsed.protocol}`);
      return "";
    }
    return url;
  } catch (error) {
    console.warn(`Invalid URL format: ${url}`);
    return "";
  }
}

/**
 * Converts a numeric Shopify ID to a GraphQL global ID format.
 * @param {string} type - Shopify type (e.g., "Product", "ProductVariant")
 * @param {string|number} id - Numeric ID
 * @returns {string|null} GraphQL global ID (gid://shopify/Type/ID) or null
 */
function convertIdToGid(type, id) {
  if (!id) return null;
  if (String(id).startsWith("gid://")) return id;
  return `gid://shopify/${type}/${id}`;
}

/**
 * Extracts the numeric ID from a Shopify GraphQL global ID.
 * @param {string} gid - GraphQL global ID (gid://shopify/Type/ID)
 * @returns {string} Numeric ID portion
 */
function extractRawId(gid) {
  return gid.split("/").pop();
}

/**
 * Formats a price value as a USD currency string.
 * @param {string|number} price - Price value
 * @returns {string} Formatted price (e.g., "$99.99") or empty string
 */
function formatPrice(price) {
  const priceValue = parseFloat(price);
  return !isNaN(priceValue) ? `$${priceValue.toFixed(2)}` : "";
}

/**
 * Formats a variant title for display.
 * Combines product title with variant title if not "Default Title".
 * @param {Object} variant - ProductVariant node from GraphQL
 * @returns {string} Formatted title
 */
function formatVariantTitle(variant) {
  if (variant.title !== "Default Title") {
    return `${variant.product.title} - ${variant.title}`;
  }
  return variant.product.title;
}

/**
 * Builds product configuration from raw configurator data.
 * Generates ID arrays and object arrays for each step definition.
 * Filters out section headers (objects without id property).
 * @param {Object} rawData - Raw configurator data object
 * @returns {Object} Normalized configuration with IDs and objects for each category
 */
function buildProductConfig(rawData) {
  const config = {};

  STEP_DEFINITIONS.forEach((step) => {
    const sourceKey = `full_${step.dataKey}_ids`;
    const sourceData = rawData[sourceKey] || [];

    // Create ID array (excluding section headers) and full objects array
    config[`${step.dataKey}Ids`] = sourceData
      .filter((item) => item.id) // Only items with actual IDs
      .map((item) => item.id);
    config[`${step.dataKey}Objects`] = sourceData; // Keep all items including headers
  });

  return config;
}

// ============================================================================
// SECTION 3: DATA LOADING & GRAPHQL
// ============================================================================

/**
 * Formats a GraphQL node (Product or ProductVariant) into application item format.
 * @param {Object} node - GraphQL node from query response
 * @param {Array} configObjects - Metadata objects for enrichment
 * @returns {Object} Formatted item with id, title, price, image, etc.
 */

function formatGraphQLNode(node, configObjects) {
  const rawId = extractRawId(node.id);
  let metadata = configObjects.find((obj) => String(obj.id) === rawId);

  // Variant nodes: fall back to metadata keyed by the parent product's ID.
  // Catalog entries often use product IDs while shared URLs encode variant
  // IDs, so an exact match can miss. Exact match always wins.
  if (!metadata && !node.variants && node.product?.id) {
    const productRawId = extractRawId(node.product.id);
    metadata = configObjects.find((obj) => String(obj.id) === productRawId);
  }
  metadata = metadata || {};

  // _sourceId must be the ID the catalog uses for this item — that's what
  // branch invalidation and catalog-order merging match against. When the
  // node was reached through a different ID (variant vs product), the
  // matched metadata's own ID is the catalog's ID.
  const sourceId = metadata.id ? String(metadata.id) : rawId;

  // Handle Product type (has variants field)
  if (node.variants) {
    const variant = node.variants.nodes[0];
    return {
      id: extractRawId(variant.id),
      _sourceId: sourceId,
      title: metadata.customTitle || node.title,
      price: variant.price.amount,
      image: node.featuredImage?.url || "",
      url: `/products/${node.handle}`,
      available: variant.availableForSale,
      infoBlurb: metadata.info || "",
      bestSeller: metadata.bestSeller === "true",
      nextStepData: metadata.nextStepData || null,
    };
  }

  // Handle ProductVariant type
  return {
    id: extractRawId(node.id),
    _sourceId: sourceId,
    title: metadata.customTitle || formatVariantTitle(node),
    price: node.price.amount,
    image: node.image?.url || node.product.featuredImage?.url || "",
    url: `/products/${node.product.handle}?variant=${extractRawId(node.id)}`,
    available: node.availableForSale,
    infoBlurb: metadata.info || "",
    bestSeller: metadata.bestSeller === "true",
    nextStepData: metadata.nextStepData || null,
  };
}

/**
 * Builds a disabled placeholder item for a catalog ID that no longer resolves
 * on the store (archived/unpublished products return null from the API).
 * Rendered as an unselectable card so the catalog entry stays visible.
 * @param {Object} configObj - Config object from configurator-data.js
 * @param {number} index - Original index within the step's config list
 * @returns {Object} Placeholder item in application item format
 */
function createPlaceholderItem(configObj, index) {
  return {
    _isPlaceholder: true,
    id: String(configObj.id),
    _sourceId: String(configObj.id),
    title:
      configObj.customTitle || configObj.personalNote || "Unavailable item",
    price: null,
    image: "",
    url: "",
    available: false,
    infoBlurb: configObj.info || "",
    bestSeller: false,
    nextStepData: null,
    _originalIndex: index,
  };
}

// Memoized flat catalog — computed once, the data file never changes at runtime
let allConfigObjectsCache = null;

/**
 * Collects every id-bearing config object across the whole catalog, including
 * objects nested inside nextStepData branches. Deduped by id, preferring the
 * entry that carries nextStepData so branch chains survive a shared-URL
 * rebuild. Section headers are excluded.
 * @returns {Array<Object>} Flat list of config objects
 */
function collectAllConfigObjects() {
  if (allConfigObjectsCache) return allConfigObjectsCache;

  const byId = new Map();
  const visit = (items) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      if (!item || typeof item !== "object") return;
      if (item.id) {
        const key = String(item.id);
        const existing = byId.get(key);
        if (!existing || (!existing.nextStepData && item.nextStepData)) {
          byId.set(key, item);
        }
      }
      if (item.nextStepData) {
        Object.values(item.nextStepData).forEach(visit);
      }
    });
  };
  Object.values(configuratorData).forEach(visit);

  allConfigObjectsCache = [...byId.values()];
  return allConfigObjectsCache;
}

/**
 * Warns about nextStepData keys that don't match any step display name.
 * Branching resolves overrides by step name, so a renamed step or a typo in
 * the data file silently disables a branch — this makes it loud in dev tools.
 * @returns {void}
 */
function validateBranchKeys() {
  const knownNames = new Set(STEP_DEFINITIONS.map((step) => step.name));
  const visit = (items) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      if (!item || typeof item !== "object" || !item.nextStepData) return;
      Object.entries(item.nextStepData).forEach(([stepName, children]) => {
        if (!knownNames.has(stepName)) {
          console.warn(
            `[Configurator] nextStepData key "${stepName}" on item ${item.id} ` +
              `does not match any step name (known: ${[...knownNames].join(", ")})`,
          );
        }
        visit(children);
      });
    });
  };
  Object.values(configuratorData).forEach(visit);
}

/**
 * Builds a display item for a section header config entry.
 * @param {Object} configObj - Config object with a sectionHeader property
 * @param {number} index - Original index within the step's config list
 * @returns {Object} Section header item
 */
function createSectionHeaderItem(configObj, index) {
  return {
    _isSectionHeader: true,
    sectionHeader: configObj.sectionHeader,
    sectionDescription: configObj.sectionDescription || "",
    _originalIndex: index,
  };
}

/**
 * Reassembles products into the catalog's original order, re-inserting
 * section headers and substituting a disabled placeholder for any catalog
 * ID that no longer resolves to a product.
 * @param {Array<Object>} products - Formatted product items
 * @param {Array<Object>} configObjects - Step config list (may include section headers)
 * @returns {Array<Object>} Products, headers, and placeholders in catalog order
 */
function mergeProductsWithConfig(products, configObjects) {
  const merged = [];
  configObjects.forEach((configObj, index) => {
    if (!configObj) return;
    if (configObj.sectionHeader) {
      merged.push(createSectionHeaderItem(configObj, index));
      return;
    }
    if (!configObj.id) return;

    const product = products.find(
      (p) =>
        p._sourceId === String(configObj.id) || p.id === String(configObj.id),
    );
    if (product) {
      product._originalIndex = index;
      merged.push(product);
    } else {
      merged.push(createPlaceholderItem(configObj, index));
    }
  });
  return merged;
}

/**
 * Final shaping shared by the cached and fetched paths. With section headers,
 * rebuild the catalog order (placeholders included); without them, append
 * placeholders only for requested IDs that resolved to nothing and have
 * config metadata to build a card from.
 * @param {Array<Object>} products - Formatted product items
 * @param {Array<string>} numericIds - The IDs that were requested
 * @param {Array<Object>} configObjects - Step config list
 * @returns {Array<Object>} Display-ready item list
 */
function finalizeProducts(products, numericIds, configObjects) {
  const hasSectionHeaders = configObjects.some(
    (obj) => obj && obj.sectionHeader,
  );
  if (hasSectionHeaders) {
    return mergeProductsWithConfig(products, configObjects);
  }

  const resolvedIds = new Set();
  products.forEach((product) => {
    resolvedIds.add(String(product.id));
    if (product._sourceId) resolvedIds.add(String(product._sourceId));
  });

  const placeholders = numericIds
    .filter((id) => !resolvedIds.has(String(id)))
    .map((id) => {
      const configObj = configObjects.find(
        (obj) => obj && String(obj.id) === String(id),
      );
      return configObj
        ? createPlaceholderItem(configObj, configObjects.indexOf(configObj))
        : null;
    })
    .filter(Boolean);

  return placeholders.length > 0 ? [...products, ...placeholders] : products;
}

/**
 * Fetches product data from Shopify GraphQL API.
 * Queries for both Product and ProductVariant types since IDs could be either.
 * Preserves section headers and renders unresolvable IDs as placeholders.
 * @param {Array<string>} numericIds - Array of product/variant IDs
 * @param {Array<Object>} configObjects - Metadata objects for enrichment (may include section headers)
 * @returns {Promise<Array>} Array of formatted product items and section headers
 */
async function fetchProductsGraphQL(numericIds, configObjects) {
  if (!numericIds || numericIds.length === 0) {
    return configObjects
      .map((obj, index) =>
        obj && obj.sectionHeader ? createSectionHeaderItem(obj, index) : null,
      )
      .filter(Boolean);
  }

  // Serve from cache where possible; only fetch what's missing
  const cachedResults = [];
  const uncachedIds = [];
  numericIds.forEach((id) => {
    if (productCache[String(id)]) {
      cachedResults.push(productCache[String(id)]);
    } else {
      uncachedIds.push(id);
    }
  });

  if (uncachedIds.length === 0) {
    return finalizeProducts(cachedResults, numericIds, configObjects);
  }

  // Generate global IDs for both Product and ProductVariant types
  const gids = uncachedIds.flatMap((id) => [
    convertIdToGid("Product", id),
    convertIdToGid("ProductVariant", id),
  ]);

  const query = `
      query getProducts($ids: [ID!]!) {
        nodes(ids: $ids) {
          ... on Product {
            id
            title
            handle
            featuredImage { url }
            variants(first: 1) {
              nodes {
                id
                price { amount }
                availableForSale
              }
            }
          }
          ... on ProductVariant {
            id
            title
            availableForSale
            price { amount }
            image { url }
            product {
              id
              title
              handle
              featuredImage { url }
            }
          }
        }
      }
    `;

  const MAX_RETRIES = 1;
  const RETRY_DELAY = 1000;

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      const response = await fetch(GRAPHQL_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
        },
        body: JSON.stringify({ query, variables: { ids: gids } }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Shopify API Error Status:", response.status);
        console.error("Shopify API Error Body:", errorText);
        throw new Error(`Network response was not ok: ${response.status}`);
      }

      const json = await response.json();
      if (json.errors) {
        console.error("GraphQL Query Errors:", json.errors);
        throw new Error(json.errors[0].message);
      }

      // Filter out nulls and format nodes
      const products = json.data.nodes
        .filter((node) => node !== null)
        .map((node) => formatGraphQLNode(node, configObjects));

      // Populate product cache
      products.forEach((p) => {
        if (p && p.id) productCache[p.id] = p;
      });

      // Deduplicate (in case we got both Product AND Variant for same ID)
      const allProducts = [...cachedResults, ...products];
      const deduplicatedProducts = Array.from(
        new Map(allProducts.map((item) => [item.id, item])).values(),
      );

      return finalizeProducts(deduplicatedProducts, numericIds, configObjects);
    } catch (error) {
      console.error(`GraphQL Fetch Failed (attempt ${attempt + 1}):`, error);
      if (attempt < MAX_RETRIES) {
        await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY));
      } else {
        // Final failure — show user-facing error
        const contentArea = document.getElementById("rf-content-area");
        if (contentArea) {
          contentArea.innerHTML =
            '<div class="rf-error-box" style="text-align:center;padding:2rem;color:#de1414;">Failed to load products. Please refresh the page to try again.</div>';
        }
        return [];
      }
    }
  }

  return [];
}

/**
 * Gets the base configuration for a step (before any filtering).
 * @param {Object} step - Step data object
 * @returns {Object} Object with ids and objects arrays
 */
function getStepBaseConfig(step) {
  return {
    ids: step._idsConfig[`${step._dataKey}Ids`],
    objects: step._idsConfig[`${step._dataKey}Objects`] || [],
  };
}

/**
 * Finds product override data from previous selections (branching logic).
 * Loops backwards through selections looking for nextStepData that matches current step.
 * Supports section headers in override data (objects without id property).
 * @param {number} currentIndex - Current step index
 * @param {string} stepName - Name of current step
 * @returns {Object|null} Override config with ids and objects, or null
 */
function findStepOverride(currentIndex, stepName) {
  for (let i = currentIndex - 1; i >= 0; i--) {
    const selection = appState.selections[i];

    // Handle both single-select (object) and multi-select (array)
    if (Array.isArray(selection)) {
      // Multi-select: check each item for nextStepData
      for (const item of selection) {
        const overrideData = item?.nextStepData?.[stepName];
        if (overrideData) {
          return {
            // Filter out section headers (items without id) when extracting IDs
            ids: overrideData
              .filter((item) => typeof item === "object" && item.id)
              .map((item) => item.id),
            // Keep all objects including section headers
            objects: overrideData.filter((item) => typeof item === "object"),
          };
        }
      }
    } else {
      // Single-select: check the item
      const overrideData = selection?.nextStepData?.[stepName];
      if (overrideData) {
        return {
          // Filter out section headers (items without id) when extracting IDs
          ids: overrideData
            .filter((item) => typeof item === "object" && item.id)
            .map((item) => item.id),
          // Keep all objects including section headers
          objects: overrideData.filter((item) => typeof item === "object"),
        };
      }
    }
  }
  return null;
}

/**
 * Checks if a step is already loaded with the specified IDs.
 * @param {Object} step - Step data object
 * @param {Array} ids - Array of product IDs
 * @returns {boolean} True if already loaded with these IDs
 */
function isAlreadyLoaded(step, ids) {
  const idKey = JSON.stringify(ids);
  return step._loaded && step._lastLoadedIds === idKey;
}

/**
 * Loads product data for a specific step on demand.
 * Applies conditional filtering based on previous selections (nextStepData).
 * @param {number} stepIndex - Index of step to load
 * @returns {Promise<void>}
 */
async function loadStepOnDemand(stepIndex) {
  const step = partsData.steps[stepIndex];
  if (!step) return;

  // Get base configuration
  const baseConfig = getStepBaseConfig(step);

  // Check for override from previous selections
  const override = findStepOverride(stepIndex, step.name);

  // Use override if found, otherwise use base config
  const finalConfig = override || baseConfig;

  // Skip if already loaded with same IDs (performance optimization)
  if (isAlreadyLoaded(step, finalConfig.ids)) {
    return;
  }

  try {
    const products = await fetchProductsGraphQL(
      finalConfig.ids,
      finalConfig.objects,
    );

    // Merge nested nextStepData from config into fetched products
    // This enables chained/nested branching (e.g., Chassis -> Stand -> Monitor)
    if (override && override.objects) {
      products.forEach((product) => {
        const configObj = override.objects.find(
          (obj) =>
            obj.id &&
            String(obj.id) === String(product._sourceId || product.id),
        );
        if (configObj && configObj.nextStepData) {
          product.nextStepData = configObj.nextStepData;
        }
      });
    }

    step.items = products;
    step._lastLoadedIds = JSON.stringify(finalConfig.ids);
    step._loaded = true;
  } catch (error) {
    console.error("Step load error:", error);
    step._loaded = true; // Mark as loaded to prevent infinite retries
  }
}

/**
 * Initializes product data structure for all steps.
 * Loads the first step's products immediately.
 * @param {Object} idsConfig - Configuration object with IDs and objects
 * @returns {Promise<Object>} Object with steps array
 */
async function loadProductDataByIds(idsConfig) {
  const steps = STEP_DEFINITIONS.map((stepDef) => ({
    name: stepDef.name,
    description: stepDef.description,
    allowMultiple: stepDef.allowMultiple || false,
    items: [],
    _dataKey: stepDef.dataKey,
    _loaded: false,
    _idsConfig: idsConfig,
  }));

  // Load first step immediately
  if (steps.length > 0) {
    const firstStep = steps[0];
    const firstStepConfig = getStepBaseConfig(firstStep);

    const firstItems = await fetchProductsGraphQL(
      firstStepConfig.ids,
      firstStepConfig.objects,
    );

    firstStep.items = firstItems;
    firstStep._loaded = true;
  }

  return { steps };
}

// ============================================================================
// SECTION 4: UI RENDERING
// ============================================================================

/**
 * Generates HTML for a product card.
 * @param {Object} item - Product item data
 * @param {boolean} isSelected - Whether this item is currently selected
 * @returns {string} HTML string for card content
 */
function createCardHTML(item, isSelected) {
  const priceDisplay = item._isPlaceholder
    ? "Unavailable"
    : formatPrice(item.price);
  const badge = item.bestSeller
    ? '<div class="rf-badge-gold">Best Seller</div>'
    : "";
  const checkmark = isSelected ? '<div class="rf-card__check">✓</div>' : "";
  // Mouse-only affordance: nested buttons are invalid inside role="button",
  // so this stays out of the tab order — the blurb is in the card's aria-label
  const infoButton = item.infoBlurb
    ? `<button class="rf-info-btn" tabindex="-1" aria-hidden="true" title="${escapeHTML(
        item.infoBlurb,
      )}">i</button>`
    : "";

  const fallbackSvg = encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 180" fill="%23ccc"><rect width="200" height="180" fill="%23f4f5f7"/><text x="50%" y="54%" text-anchor="middle" font-size="14" fill="%23999">No Image</text></svg>',
  );

  return `
    ${badge}
    <div class="rf-card__image-box">
      <img src="${escapeURL(item.image)}" alt="${escapeHTML(
        item.title,
      )}" class="rf-card__image" onerror="this.onerror=null;this.src='data:image/svg+xml,${fallbackSvg}';">
    </div>
    <div class="rf-card__info">
      <div class="rf-card__title">${escapeHTML(item.title)}</div>
      <div class="rf-card__price">${priceDisplay}</div>
    </div>
    <div class="rf-card__actions">
      ${infoButton}
      ${checkmark}
    </div>
  `;
}

/**
 * Attaches event handlers to a product card.
 * @param {HTMLElement} card - Card DOM element
 * @param {Object} item - Product item data
 * @returns {void}
 */
function attachCardEventHandlers(card, item) {
  const handleAction = (event) => {
    // role="button" must respond to both Enter and Space
    if (event.type === "keydown") {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault(); // stop Space from scrolling the page
    }

    // Handle info button separately (alert renders plain text — no escaping)
    if (event.target.classList.contains("rf-info-btn")) {
      alert(item.infoBlurb);
      event.stopPropagation();
      return;
    }

    // Select the product
    selectProduct(item, card);
    saveProgress();
  };

  card.addEventListener("click", handleAction);
  card.addEventListener("keydown", handleAction);
}

/**
 * Creates a product card DOM element.
 * @param {Object} item - Product item data
 * @returns {HTMLElement} Card DOM element
 */
function createProductCard(item) {
  const currentSelection = appState.selections[appState.currentStep];

  // Handle both single-select (object) and multi-select (array)
  let isSelected = false;
  if (Array.isArray(currentSelection)) {
    isSelected = currentSelection.some((sel) => sel.id === item.id);
  } else if (currentSelection) {
    isSelected = currentSelection.id === item.id;
  }

  const card = document.createElement("div");
  card.className = "rf-card";
  card.setAttribute("tabindex", "0");
  card.setAttribute("role", "button");
  card.setAttribute(
    "aria-label",
    item.infoBlurb ? `${item.title}. ${item.infoBlurb}` : item.title,
  );
  card.setAttribute("aria-pressed", String(isSelected));
  card.setAttribute("aria-disabled", String(!item.available));
  card.setAttribute("data-best-seller", String(item.bestSeller));

  if (!item.available) {
    card.classList.add("rf-oos");
  }
  if (item._isPlaceholder) {
    card.classList.add("rf-card--placeholder");
  }
  if (isSelected) {
    card.classList.add("rf-card--selected");
  }
  if (item.bestSeller) {
    card.classList.add("rf-card--bestseller");
  }

  card.innerHTML = createCardHTML(item, isSelected);

  if (item.available) {
    attachCardEventHandlers(card, item);
  }

  return card;
}

/**
 * Renders the current step's content area.
 * Shows loading state, product cards, or review page based on app state.
 * @returns {Promise<void>}
 */
/**
 * Checks if the viewport is mobile-sized.
 * @returns {boolean}
 */
function isMobileView() {
  return window.innerWidth <= 900;
}

/**
 * Clears all mobile accordion panels.
 * @returns {void}
 */
function clearAccordionPanels() {
  for (let i = 0; i < appState.totalSteps; i++) {
    const panel = document.getElementById(`nav-panel-${i}`);
    if (panel) {
      panel.innerHTML = "";
      panel.classList.remove("rf-nav__panel--open");
    }
  }
}

/**
 * Renders product items into a target container.
 * @param {HTMLElement} target - Container to render into
 * @param {Object} stepData - Step data with items array
 * @returns {void}
 */
function renderItemsInto(target, stepData) {
  if (stepData.items.length === 0) {
    target.innerHTML = '<div class="rf-loading">No products found.</div>';
    return;
  }

  const sortedItems = sortItemsBySection(stepData.items);

  sortedItems.forEach((item) => {
    if (item._isSectionHeader) {
      const header = document.createElement("div");
      header.className = "rf-section-header";

      const title = document.createElement("h3");
      title.className = "rf-section-header__title";
      title.textContent = item.sectionHeader;
      header.appendChild(title);

      if (item.sectionDescription) {
        const description = document.createElement("p");
        description.className = "rf-section-header__description";
        description.textContent = item.sectionDescription;
        header.appendChild(description);
      }

      target.appendChild(header);
    } else {
      target.appendChild(createProductCard(item));
    }
  });
}

async function renderCurrentStep() {
  const contentArea = rfElements.contentArea;
  if (!contentArea) return;

  contentArea.innerHTML = "";
  clearAccordionPanels();
  updateProgressCounter();

  // Show review page if in review mode (always in main content area)
  if (appState.isReviewStep) {
    contentArea.classList.remove("rf-content--accordion-active");
    await renderReviewPage(contentArea);
    updateNavigationButtons();
    return;
  }

  const currentStepData = partsData.steps[appState.currentStep];

  // Load step data if not already loaded
  if (!currentStepData._loaded) {
    const loadingHtml = '<div class="rf-loading-spinner"><div class="rf-loading-spinner__circle"></div><div class="rf-loading-spinner__text">Loading components...</div></div>';
    contentArea.innerHTML = loadingHtml;
    await loadStepOnDemand(appState.currentStep);
    contentArea.innerHTML = "";
  }

  // On mobile: render into the accordion panel under the active step
  if (isMobileView()) {
    contentArea.classList.add("rf-content--accordion-active");
    const panel = document.getElementById(`nav-panel-${appState.currentStep}`);
    if (panel) {
      panel.classList.add("rf-nav__panel--open");
      renderItemsInto(panel, currentStepData);
    }
  } else {
    // Desktop: render into the main content area
    contentArea.classList.remove("rf-content--accordion-active");
    renderItemsInto(contentArea, currentStepData);
  }

  updateNavigationButtons();
}

/**
 * Sorts items within each section group independently.
 * Section headers act as group dividers.
 * @param {Array} items - Array of items including section headers
 * @returns {Array} Sorted items with sections preserved
 */
function sortItemsBySection(items) {
  const result = [];
  let currentGroup = [];

  // Helper to sort a group: in-stock first, then alphabetically
  const sortGroup = (group) => {
    return [...group].sort((a, b) => {
      // Availability: in-stock comes first
      if (a.available !== b.available) {
        return a.available ? -1 : 1;
      }
      // Alphabetical by title
      return (a.title || "").localeCompare(b.title || "");
    });
  };

  items.forEach((item) => {
    if (item._isSectionHeader) {
      // Sort and flush the current group before the header
      if (currentGroup.length > 0) {
        result.push(...sortGroup(currentGroup));
        currentGroup = [];
      }
      // Add the header itself
      result.push(item);
    } else {
      // Accumulate items in the current group
      currentGroup.push(item);
    }
  });

  // Don't forget the last group after all headers
  if (currentGroup.length > 0) {
    result.push(...sortGroup(currentGroup));
  }

  return result;
}

/**
 * Renders the review page showing all selections.
 * Re-validates availability and flags unavailable items.
 * @param {HTMLElement} container - Container element to render into
 * @returns {Promise<void>}
 */
async function renderReviewPage(container) {
  // Show progress while re-checking live availability
  const progressEl = document.createElement("div");
  progressEl.className = "rf-loading-spinner";
  progressEl.innerHTML = '<div class="rf-loading-spinner__circle"></div><div class="rf-loading-spinner__text">Checking availability...</div>';
  container.appendChild(progressEl);

  const failureMap = await validateSelectionAvailability();

  container.innerHTML = "";

  const list = document.createElement("div");
  list.className = "rf-review-grid";

  /**
   * Creates a single review card for one item.
   */
  function createReviewCard(stepName, itemData) {
    const itemId = String(itemData.id);
    const isUnavailable = failureMap.has(itemId);
    const failReason = failureMap.get(itemId) || "";
    const priceDisplay = formatPrice(itemData.price);
    const card = document.createElement("div");
    card.className = `rf-review-item${isUnavailable ? " rf-review-item--unavailable" : ""}`;

    const warningHtml = isUnavailable
      ? `<div class="rf-review-item__warning">Cannot add to cart — ${escapeHTML(failReason)}</div>`
      : "";

    card.innerHTML = `
      <div class="rf-review-item__step">${escapeHTML(stepName)}</div>
      ${warningHtml}
      <div class="rf-review-item__detail">
        <img src="${escapeURL(itemData.image)}" alt="${escapeHTML(itemData.title)}" class="rf-review-item__img"/>
        <div class="rf-review-item__info">
          <div class="rf-review-item__title">${escapeHTML(itemData.title)}</div>
          <div class="rf-review-item__price">${priceDisplay}</div>
          <a href="${escapeURL(itemData.url)}" target="_blank" rel="noopener noreferrer" class="rf-review-item__link">View Product &rarr;</a>
        </div>
      </div>
    `;

    // Mark unavailable so addAllToCart skips it
    itemData.available = !isUnavailable;

    return card;
  }

  /**
   * Creates an empty/skipped review card.
   */
  function createEmptyCard(stepName, label) {
    const card = document.createElement("div");
    card.className = "rf-review-item rf-review-item--empty";
    card.innerHTML = `
      <div class="rf-review-item__step">${escapeHTML(stepName)}</div>
      <div class="rf-review-item__detail">
        <span class="rf-review-empty">${label}</span>
      </div>
    `;
    return card;
  }

  partsData.steps.forEach((step, index) => {
    const selection = appState.selections[index];
    const isSkipped = appState.skippedSteps[index];

    if (isSkipped) {
      list.appendChild(createEmptyCard(step.name, "Skipped"));
    } else if (Array.isArray(selection) && selection.length > 0) {
      selection.forEach((subItem) => {
        list.appendChild(createReviewCard(step.name, subItem));
      });
    } else if (selection && !Array.isArray(selection)) {
      list.appendChild(createReviewCard(step.name, selection));
    } else {
      list.appendChild(createEmptyCard(step.name, "Not selected"));
    }
  });

  container.appendChild(list);
}

// ============================================================================
// SECTION 5: UI UPDATES & STATE
// ============================================================================

/**
 * Flattens appState.selections (single items and multi-select arrays) into
 * one list of selected items with their step context.
 * @returns {Array<{stepIndex: number, stepName: string, item: Object}>}
 */
function collectSelectedItems() {
  const selected = [];
  Object.entries(appState.selections).forEach(([stepIndex, selection]) => {
    const step = partsData.steps[stepIndex];
    if (!step || !selection) return;

    const items = Array.isArray(selection) ? selection : [selection];
    items.forEach((item) => {
      if (item) {
        selected.push({ stepIndex: Number(stepIndex), stepName: step.name, item });
      }
    });
  });
  return selected;
}

/**
 * Drops downstream selections that are no longer valid for the current
 * branch. After a selection changes on a step, each later step's applicable
 * item list may change (nextStepData overrides) — any picked item that isn't
 * in its step's new list is removed. Steps are processed in order because
 * clearing a selection at step i changes override resolution for steps after
 * it. Skipped steps are left alone (a skip is branch-independent).
 * @param {number} changedStepIndex - Step whose selection just changed
 * @returns {void}
 */
function invalidateDownstreamSelections(changedStepIndex) {
  for (let i = changedStepIndex + 1; i < partsData.steps.length; i++) {
    const step = partsData.steps[i];
    const selection = appState.selections[i];
    if (!step || !selection) continue;

    const config = findStepOverride(i, step.name) || getStepBaseConfig(step);
    const validIds = new Set(config.ids.map(String));
    const isValid = (item) =>
      item && validIds.has(String(item._sourceId || item.id));

    if (Array.isArray(selection)) {
      const kept = selection.filter(isValid);
      if (kept.length === 0) {
        delete appState.selections[i];
      } else if (kept.length !== selection.length) {
        appState.selections[i] = kept;
      }
    } else if (!isValid(selection)) {
      delete appState.selections[i];
    }
  }
}

/**
 * Handles product selection.
 * Updates UI, marks future steps for reloading, drops downstream selections
 * invalidated by the new branch, and updates furthest step reached.
 * @param {Object} item - Selected product item
 * @param {HTMLElement} cardElement - Card DOM element that was clicked
 * @returns {void}
 */
function selectProduct(item, cardElement) {
  // Guard: prevent selection of out-of-stock items regardless of CSS state
  if (item.available === false) return;

  // Disable swipe observer during selection to prevent unwanted re-renders
  appState.programmaticScrollCount++;

  const currentStepData = partsData.steps[appState.currentStep];
  const allowMultiple = currentStepData.allowMultiple || false;

  if (allowMultiple) {
    // MULTI-SELECT MODE: Toggle item in/out of array
    if (!Array.isArray(appState.selections[appState.currentStep])) {
      appState.selections[appState.currentStep] = [];
    }

    const currentSelections = appState.selections[appState.currentStep];
    const existingIndex = currentSelections.findIndex(
      (sel) => sel.id === item.id,
    );

    if (existingIndex >= 0) {
      // Already selected - remove it
      currentSelections.splice(existingIndex, 1);
      cardElement.classList.remove("rf-card--selected");
      cardElement.setAttribute("aria-pressed", "false");

      // If no items left, treat as skipped
      if (currentSelections.length === 0) {
        delete appState.selections[appState.currentStep];
      }
    } else {
      // Not selected - add it
      currentSelections.push(item);
      cardElement.classList.add("rf-card--selected");
      cardElement.setAttribute("aria-pressed", "true");
    }
  } else {
    // SINGLE-SELECT MODE: Replace selection
    document.querySelectorAll(".rf-card").forEach((card) => {
      card.classList.remove("rf-card--selected");
      card.setAttribute("aria-pressed", "false");
    });
    cardElement.classList.add("rf-card--selected");
    cardElement.setAttribute("aria-pressed", "true");
    appState.selections[appState.currentStep] = item;
  }

  // Clear skip status when making a selection
  delete appState.skippedSteps[appState.currentStep];

  // Mark future steps as not loaded (they may need to reload with filtered products)
  for (let i = appState.currentStep + 1; i < partsData.steps.length; i++) {
    if (partsData.steps[i]) {
      partsData.steps[i]._loaded = false;
    }
  }

  // Drop downstream picks that don't exist in the new branch's lists
  invalidateDownstreamSelections(appState.currentStep);

  // Update furthest step reached
  if (appState.currentStep === appState.furthestStep) {
    appState.furthestStep = appState.currentStep + 1;
  }

  updateCartSummary();
  updateSidebarVisuals();
  updateNavigationButtons();
  pulseDrawerTab();

  // Re-enable swipe observer after UI updates complete
  setTimeout(() => {
    appState.programmaticScrollCount--;
  }, SELECTION_SCROLL_LOCK_MS);
}

/**
 * Updates visual state of sidebar navigation items.
 * Sets active/completed/disabled classes and scrolls active item into view.
 * @returns {void}
 */
function updateSidebarVisuals() {
  if (!rfElements.navList) return;

  for (let i = 0; i < appState.totalSteps; i++) {
    const button = document.getElementById(`nav-step-${i}`);
    const label = button?.querySelector(".rf-nav__label");
    if (!button || !label) continue;

    const step = partsData.steps[i];
    const selection = appState.selections[i];
    const isSkipped = appState.skippedSteps[i];

    // Reset classes and ARIA state
    button.classList.remove(
      "rf-nav__item--active",
      "rf-nav__item--completed",
      "rf-nav__item--disabled",
    );
    button.setAttribute("aria-selected", "false");

    // Multi-select badge (preserved across label updates)
    const badge = step.allowMultiple
      ? '<span class="rf-multi-icon"></span>'
      : '';

    // Update label text
    if (Array.isArray(selection)) {
      // Multi-select: show count
      if (selection.length > 0) {
        const count = selection.length;
        const titles = selection.map((s) => s.title).join(", ");
        label.innerHTML = `${badge}${step.name}<span class="rf-nav__selection">${count} selected</span>`;
        label.title = titles;
      } else {
        label.innerHTML = `${badge}${step.name}`;
      }
    } else if (selection) {
      // Single-select: show title
      label.innerHTML = `${badge}${step.name}<span class="rf-nav__selection">${selection.title}</span>`;
    } else if (isSkipped) {
      label.innerHTML = `${badge}${step.name}<span class="rf-nav__selection">Skipped</span>`;
    } else {
      label.innerHTML = `${badge}${step.name}`;
    }

    // Update classes based on state
    if (appState.isReviewStep) {
      if (selection || isSkipped) {
        button.classList.add("rf-nav__item--completed");
      }
    } else {
      if (i === appState.currentStep) {
        button.classList.add("rf-nav__item--active");
        button.setAttribute("aria-selected", "true");

        // Scroll into view (with programmatic lock to prevent observer loops)
        appState.programmaticScrollCount++;
        button.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });

        // Release lock after animation completes
        setTimeout(() => {
          appState.programmaticScrollCount--;
        }, NAV_SCROLL_LOCK_MS);
      } else if (i < appState.currentStep || selection || isSkipped) {
        button.classList.add("rf-nav__item--completed");
        button.setAttribute("tabindex", "0");
      } else if (i > appState.furthestStep) {
        button.classList.add("rf-nav__item--disabled");
        button.setAttribute("tabindex", "-1");
      }
    }
  }
}

/**
 * Gets navigation context (state information for button rendering).
 * @returns {Object} Context object with current state flags
 */
function getNavigationContext() {
  const currentStep = appState.currentStep;
  const hasSelection = Boolean(appState.selections[currentStep]);
  const isSkipped = Boolean(appState.skippedSteps[currentStep]);
  const isLastStep = currentStep === appState.totalSteps - 1;
  const isReview = appState.isReviewStep;

  const selectedCount = Object.keys(appState.selections).length;
  const skippedCount = Object.keys(appState.skippedSteps).length;
  const isBuildComplete = selectedCount + skippedCount === appState.totalSteps;
  const hasAnySelection = selectedCount > 0;

  return {
    hasSelection,
    isSkipped,
    isLastStep,
    isReview,
    isBuildComplete,
    hasAnySelection,
    canProceed: hasSelection || isSkipped,
  };
}

/**
 * Updates visibility of navigation buttons based on context.
 * @param {Object} context - Navigation context object
 * @returns {void}
 */
function updateButtonVisibility(context) {
  // Jump to Review button (shown when build is complete but not on last step)
  const showJumpReview =
    context.isBuildComplete && !context.isReview && !context.isLastStep;
  rfElements.jumpReviewBtn.classList.toggle("rf-hidden", !showJumpReview);

  // Skip button (hidden on review page)
  rfElements.skipBtn.classList.toggle("rf-hidden", context.isReview);

  // Share button (shown only on review page)
  rfElements.shareBtn.textContent = "Share Build";
  rfElements.shareBtn.classList.toggle("rf-hidden", !context.isReview);

  // Copy Checkout Link (shown only on review, beside Add to Cart)
  if (rfElements.checkoutLinkBtn) {
    rfElements.checkoutLinkBtn.classList.toggle("rf-hidden", !context.isReview);
  }

  // Footer Share/Clear — review only, and only while the drawer is closed
  // (the open drawer shows its own copies; mobile has no drawer at all)
  const showFooterBuildActions =
    context.isReview && (isMobileView() || !isBuildDrawerOpen());
  if (rfElements.footerShareBtn) {
    rfElements.footerShareBtn.classList.toggle(
      "rf-hidden",
      !showFooterBuildActions,
    );
  }
  if (rfElements.footerResetBtn) {
    rfElements.footerResetBtn.classList.toggle(
      "rf-hidden",
      !showFooterBuildActions,
    );
  }
}

/**
 * Updates state of navigation buttons (enabled/disabled, text).
 * @param {Object} context - Navigation context object
 * @returns {void}
 */
function updateButtonStates(context) {
  // Previous button (disabled on first step when not in review)
  rfElements.prevBtn.disabled = !context.isReview && appState.currentStep === 0;

  // Next button
  if (context.isReview) {
    rfElements.nextBtn.textContent = "Add to Cart";
    rfElements.nextBtn.disabled = false;
  } else {
    rfElements.nextBtn.textContent = context.isLastStep ? "Review" : "Next";
    rfElements.nextBtn.disabled = !context.canProceed;
  }
}

/**
 * Handles previous button click.
 * @param {boolean} isReview - Whether currently on review page
 * @returns {void}
 */
function handlePreviousClick(isReview) {
  if (isReview) {
    appState.isReviewStep = false;
    appState.currentStep = appState.totalSteps - 1;
  } else if (appState.currentStep > 0) {
    appState.currentStep--;
  }
  updateSidebarVisuals();
  renderCurrentStep();
}

/**
 * Handles skip button click.
 * @param {boolean} isLastStep - Whether on the last step
 * @returns {void}
 */
function handleSkipClick(isLastStep) {
  const skippedStep = appState.currentStep;
  delete appState.selections[skippedStep];
  appState.skippedSteps[skippedStep] = true;

  // Removing a selection can change which branch applies to later steps
  invalidateDownstreamSelections(skippedStep);

  if (skippedStep === appState.furthestStep) {
    appState.furthestStep++;
  }

  if (isLastStep) {
    appState.isReviewStep = true;
  } else {
    appState.currentStep++;
  }

  updateSidebarVisuals();
  renderCurrentStep();
  updateCartSummary();
  saveProgress();
}

/**
 * Handles next button click.
 * @param {Object} context - Navigation context object
 * @returns {void}
 */
function handleNextClick(context) {
  if (context.isReview) {
    addAllToCart();
    return;
  } else if (context.isLastStep) {
    appState.isReviewStep = true;
  } else {
    appState.currentStep++;
  }
  updateSidebarVisuals();
  renderCurrentStep();
}

/**
 * Copies a URL to the clipboard with "Copied!" feedback on the button.
 * Falls back to a prompt when clipboard access is denied.
 * @param {HTMLElement} button - Button that triggered the copy
 * @param {string} url - URL to copy
 * @param {string} promptLabel - Label for the fallback prompt
 * @returns {Promise<void>}
 */
async function copyLinkWithFeedback(button, url, promptLabel) {
  try {
    await navigator.clipboard.writeText(url);
  } catch (error) {
    prompt(promptLabel, url);
    return;
  }

  const originalText = button.textContent;
  button.textContent = "Copied!";
  button.style.background = "var(--rf-success)";
  button.style.color = "var(--rf-white)";
  button.style.borderColor = "var(--rf-success)";
  setTimeout(() => {
    button.textContent = originalText;
    button.style.background = "";
    button.style.color = "";
    button.style.borderColor = "";
  }, 2000);
}

/**
 * Handles share button click — copies the config URL to clipboard.
 * Works from either share button (drawer or footer).
 * @param {Event} event - Click event
 * @returns {Promise<void>}
 */
function handleShareClick(event) {
  return copyLinkWithFeedback(
    event.currentTarget,
    buildConfigUrl(),
    "Copy this URL to share your build:",
  );
}

/**
 * Handles checkout-link button click — copies the cart permalink.
 * @returns {Promise<void>}
 */
function handleCheckoutLinkClick() {
  return copyLinkWithFeedback(
    rfElements.checkoutLinkBtn,
    buildShareableUrl(),
    "Copy this checkout URL:",
  );
}

/**
 * Attaches click handlers to navigation buttons once during init.
 * Handlers read from module-level navContext so they always use current state.
 * @returns {void}
 */
function attachButtonHandlersOnce() {
  rfElements.prevBtn.onclick = () => handlePreviousClick(navContext.isReview);
  rfElements.skipBtn.onclick = () => handleSkipClick(navContext.isLastStep);
  rfElements.nextBtn.onclick = () => handleNextClick(navContext);
  rfElements.shareBtn.onclick = handleShareClick;
  if (rfElements.checkoutLinkBtn) {
    rfElements.checkoutLinkBtn.onclick = handleCheckoutLinkClick;
  }
  if (rfElements.footerShareBtn) {
    rfElements.footerShareBtn.onclick = handleShareClick;
  }
  if (rfElements.footerResetBtn) {
    rfElements.footerResetBtn.onclick = resetConfigurator;
  }
  if (rfElements.drawerTab) {
    rfElements.drawerTab.onclick = toggleBuildDrawer;
  }
}

/**
 * Updates all navigation buttons based on current state.
 * @returns {void}
 */
function updateNavigationButtons() {
  if (!rfElements.nextBtn) return;

  navContext = getNavigationContext();

  updateButtonVisibility(navContext);
  updateButtonStates(navContext);
}

/**
 * Updates cart total summary display.
 * @returns {void}
 */
function updateCartSummary() {
  const total = collectSelectedItems().reduce(
    (sum, { item }) => sum + (parseFloat(item.price) || 0),
    0,
  );

  const formatted = `$${total.toFixed(2)}`;

  if (rfElements.totalDisplay) {
    rfElements.totalDisplay.textContent = formatted;
  }
  if (rfElements.footerTotalDisplay) {
    rfElements.footerTotalDisplay.textContent = formatted;
  }
  if (rfElements.buildTotalDisplay) {
    rfElements.buildTotalDisplay.textContent = formatted;
  }

  updateDrawerItems();
}

/**
 * Reports whether the desktop build drawer is currently open.
 * @returns {boolean}
 */
function isBuildDrawerOpen() {
  return Boolean(
    rfElements.appContainer?.classList.contains("rf-app--drawer-open"),
  );
}

/**
 * Opens or closes the desktop build drawer and remembers the choice.
 * Footer CTAs cover the drawer's actions while it's closed, so button
 * visibility re-evaluates on every toggle.
 * @param {boolean} isOpen - Whether the drawer should be open
 * @returns {void}
 */
function setBuildDrawerOpen(isOpen) {
  const app = rfElements.appContainer;
  if (!app || !rfElements.drawerTab) return;

  app.classList.toggle("rf-app--drawer-open", isOpen);
  rfElements.drawerTab.setAttribute("aria-expanded", String(isOpen));
  updateNavigationButtons();

  try {
    sessionStorage.setItem(DRAWER_STATE_KEY, isOpen ? "1" : "0");
  } catch (error) {
    // sessionStorage unavailable — drawer still works, state just isn't remembered
  }
}

/**
 * Toggles the desktop build drawer via its edge tab.
 * @returns {void}
 */
function toggleBuildDrawer() {
  setBuildDrawerOpen(!isBuildDrawerOpen());
}

/**
 * Pulses the drawer tab so a newly added item registers while the
 * drawer is closed. No-op when the drawer is open.
 * @returns {void}
 */
function pulseDrawerTab() {
  const tab = rfElements.drawerTab;
  const app = rfElements.appContainer;
  if (!tab || !app || app.classList.contains("rf-app--drawer-open")) return;

  tab.classList.remove("rf-drawer__tab--pulse");
  void tab.offsetWidth; // restart the animation if it's mid-run
  tab.classList.add("rf-drawer__tab--pulse");
}

/**
 * Renders selected items into the right build drawer.
 * @returns {void}
 */
function updateDrawerItems() {
  const container = rfElements.drawerItems;
  if (!container) return;

  const html = collectSelectedItems().map(
    ({ stepName, item }) => `
        <div class="rf-drawer__item">
          <img src="${escapeURL(item.image)}" alt="" class="rf-drawer__item-img">
          <div class="rf-drawer__item-info">
            <div class="rf-drawer__item-step">${escapeHTML(stepName)}</div>
            <div class="rf-drawer__item-title">${escapeHTML(item.title)}</div>
            <div class="rf-drawer__item-price">${formatPrice(item.price)}</div>
          </div>
        </div>
      `,
  );

  container.innerHTML =
    html.length > 0
      ? html.join("")
      : '<p class="rf-drawer__empty">No items selected yet</p>';
}

/**
 * Makes a click-only step-jump element (tracker label or pip) fully
 * keyboard operable: focusable, announced as a button, Enter/Space activate.
 * @param {HTMLElement} element - Tracker label or pip
 * @param {number} stepIndex - Step it jumps to
 * @param {string} stepName - Step name for the accessible label
 * @returns {void}
 */
function makeStepJumpAccessible(element, stepIndex, stepName) {
  element.setAttribute("role", "button");
  element.setAttribute("tabindex", "0");
  element.setAttribute("aria-label", `Go to step ${stepIndex + 1}: ${stepName}`);
  element.addEventListener("keydown", (event) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    goToStep(stepIndex);
  });
}

/**
 * Updates progress counter and progress tracker display.
 * Creates a Domino's-style bar on desktop, pips on mobile.
 * @returns {void}
 */
function updateProgressCounter() {
  if (!rfElements.stepCounter) return;

  if (appState.isReviewStep) {
    rfElements.stepCounter.textContent = "Build Review";
    if (rfElements.pipsContainer) {
      rfElements.pipsContainer.style.display = "none";
    }
  } else {
    // Update text counter
    const current = appState.currentStep + 1;
    const total = appState.totalSteps;
    rfElements.stepCounter.textContent = `Step ${current} of ${total}`;

    if (rfElements.pipsContainer) {
      rfElements.pipsContainer.style.display = "flex";
      rfElements.pipsContainer.innerHTML = "";

      const isMobile = window.innerWidth <= 900;
      const stepLabels = STEP_DEFINITIONS.map((s) => s.name);

      if (isMobile) {
        // Mobile: Simple pips
        for (let i = 0; i < appState.totalSteps; i++) {
          const pip = document.createElement("div");
          pip.className = "rf-tracker-pip";

          const isSelected = appState.selections[i] || appState.skippedSteps[i];
          const isActive = i === appState.currentStep;
          const isCompleted = i < appState.currentStep || isSelected;

          if (isActive) pip.classList.add("rf-tracker-pip--active");
          if (isCompleted) pip.classList.add("rf-tracker-pip--completed");

          // Click handler
          if (i <= appState.furthestStep || i < appState.currentStep) {
            pip.style.cursor = "pointer";
            pip.addEventListener("click", () => goToStep(i));
            makeStepJumpAccessible(pip, i, stepLabels[i] || `Step ${i + 1}`);
          }

          rfElements.pipsContainer.appendChild(pip);
        }
      } else {
        // Desktop: Domino's-style bar
        const bar = document.createElement("div");
        bar.className = "rf-tracker-bar";

        // Create labels container
        const labelsContainer = document.createElement("div");
        labelsContainer.className = "rf-tracker-labels";

        for (let i = 0; i < appState.totalSteps; i++) {
          const label = document.createElement("div");
          label.className = "rf-tracker-label";
          const stepDef = STEP_DEFINITIONS[i];
          const trackerBadge = stepDef && stepDef.allowMultiple
            ? '<span class="rf-multi-icon"></span>'
            : '';
          label.innerHTML = `${trackerBadge}${stepLabels[i] || `Step ${i + 1}`}`;

          const isSelected = appState.selections[i] || appState.skippedSteps[i];
          const isActive = i === appState.currentStep;
          const isCompleted = i < appState.currentStep || isSelected;

          if (isActive) label.classList.add("rf-tracker-label--active");
          if (isCompleted && !isActive)
            label.classList.add("rf-tracker-label--completed");

          // Click handler for accessible steps
          if (i <= appState.furthestStep || i < appState.currentStep) {
            label.addEventListener("click", () => goToStep(i));
            makeStepJumpAccessible(label, i, stepLabels[i] || `Step ${i + 1}`);
          } else {
            label.style.opacity = "0.4";
            label.style.cursor = "not-allowed";
            label.setAttribute("aria-disabled", "true");
          }

          labelsContainer.appendChild(label);
        }

        bar.appendChild(labelsContainer);
        rfElements.pipsContainer.appendChild(bar);
      }
    }
  }
}

// ============================================================================
// SECTION 6: NAVIGATION & INTERACTION
// ============================================================================

/**
 * Jumps to a step, leaving review mode if active.
 * @param {number} stepIndex - Step to make current
 * @returns {void}
 */
function goToStep(stepIndex) {
  appState.currentStep = stepIndex;
  appState.isReviewStep = false;
  updateSidebarVisuals();
  renderCurrentStep();
}

/**
 * Sets up mobile swipe observer for navigation.
 * Uses IntersectionObserver to detect which nav item is in view.
 * @param {HTMLElement} navList - Navigation list container
 * @returns {void}
 */
function setupMobileSwipeObserver(navList) {
  if (window.innerWidth > 900) return;

  // Create observer only once; on re-render just re-observe new nav items
  if (!navObserver) {
    navObserver = new IntersectionObserver(
      (entries) => {
        if (appState.programmaticScrollCount > 0) return;

        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          const stepIndex = parseInt(
            entry.target.id.replace("nav-step-", ""),
            10,
          );

          // Only trigger if new step and within reach
          if (
            stepIndex !== appState.currentStep &&
            stepIndex <= appState.furthestStep
          ) {
            entry.target.click();
          }
        });
      },
      {
        root: navList,
        threshold: 0.6, // Item must be 60% visible to trigger
      },
    );
  }

  // Re-observe current nav items (handles re-renders)
  navObserver.disconnect();
  for (let i = 0; i < appState.totalSteps; i++) {
    const button = document.getElementById(`nav-step-${i}`);
    if (button) navObserver.observe(button);
  }
}

/**
 * Initializes sidebar navigation.
 * Creates nav buttons for each step and sets up mobile observer.
 * @returns {void}
 */
function initializeSidebarNav() {
  if (!rfElements.navList) initComponentElements();
  if (!rfElements.navList) return;

  rfElements.navList.innerHTML = "";

  // Create navigation button for each step
  for (let i = 0; i < appState.totalSteps; i++) {
    const step = partsData.steps[i];
    const button = document.createElement("button");
    button.className = "rf-nav__item";
    button.id = `nav-step-${i}`;
    button.setAttribute("role", "tab");
    button.setAttribute("aria-selected", "false");
    button.setAttribute("aria-controls", "rf-content-area");
    const multiBadge = step.allowMultiple
      ? '<span class="rf-multi-icon"></span>'
      : '';
    button.innerHTML = `<span class="rf-nav__label">${multiBadge}${step.name}</span>`;

    button.addEventListener("click", () => {
      if (i <= appState.furthestStep) goToStep(i);
    });

    rfElements.navList.appendChild(button);

    // Accordion panel for mobile — cards render here on small screens
    const panel = document.createElement("div");
    panel.className = "rf-nav__panel";
    panel.id = `nav-panel-${i}`;
    rfElements.navList.appendChild(panel);
  }

  // Set up mobile swipe observer
  setupMobileSwipeObserver(rfElements.navList);

  updateSidebarVisuals();
}

/**
 * Resets the configurator to initial state.
 * Clears all selections, skips, and reloads first step.
 * @returns {void}
 */
function resetConfigurator() {
  if (!confirm("Clear your current build and start from the beginning?")) {
    return;
  }

  // Clear session storage
  sessionStorage.removeItem("rf_build_state");

  // Reset app state
  appState.selections = {};
  appState.skippedSteps = {};
  appState.currentStep = 0;
  appState.furthestStep = 0;
  appState.isReviewStep = false;

  // Reset step data
  partsData.steps.forEach((step) => {
    step._loaded = false;
    step._lastLoadedIds = null;
    step.items = [];
  });

  // Update UI
  initializeSidebarNav();
  updateSidebarVisuals();
  renderCurrentStep();
  updateCartSummary();
}

// ============================================================================
// SECTION 7: CART & SHARING
// ============================================================================

/**
 * Validates availability of all selected items with one live Storefront API
 * query. Deliberately bypasses productCache — the check must reflect current
 * stock — and never touches the shopper's cart. Fails open on network errors
 * so a transient API blip doesn't block checkout; the add-to-cart error
 * handling is the backstop for anything this misses.
 * @returns {Promise<Map<string, string>>} Map of variantId → error reason for failures
 */
async function validateSelectionAvailability() {
  const variantIds = collectSelectedItems()
    .filter(({ item }) => item.id && !item._isPlaceholder)
    .map(({ item }) => String(item.id));

  if (variantIds.length === 0) return new Map();

  // onlineStoreUrl is null when the product isn't published to the Online
  // Store channel — the Storefront API can still see it, but /cart/add.js
  // rejects it with "Cannot find variant"
  const query = `
    query checkAvailability($ids: [ID!]!) {
      nodes(ids: $ids) {
        ... on ProductVariant {
          id
          availableForSale
          product { onlineStoreUrl }
        }
      }
    }
  `;
  const gids = variantIds.map((id) => convertIdToGid("ProductVariant", id));

  const failures = new Map();

  try {
    const response = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": STOREFRONT_ACCESS_TOKEN,
      },
      body: JSON.stringify({ query, variables: { ids: gids } }),
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const json = await response.json();
    if (json.errors) throw new Error(json.errors[0].message);

    const statusById = new Map();
    json.data.nodes.forEach((node) => {
      if (node && node.id) {
        statusById.set(extractRawId(node.id), {
          available: node.availableForSale,
          published: Boolean(node.product?.onlineStoreUrl),
        });
      }
    });

    variantIds.forEach((variantId) => {
      const status = statusById.get(variantId);
      if (!status) {
        failures.set(variantId, "Product no longer available");
      } else if (!status.published) {
        failures.set(variantId, "Not available in the online store");
      } else if (!status.available) {
        failures.set(variantId, "Out of stock");
      }
    });
  } catch (error) {
    console.warn("[Validate] Availability check failed:", error.message);
    return new Map();
  }

  return failures;
}

/**
 * Builds a shareable cart URL with selected products.
 * Extracts numeric variant IDs, filters OOS items, and uses Shopify cart format.
 * @returns {string} Shopify cart URL
 */
function buildShareableUrl() {
  const excluded = [];
  const cartLines = [];

  collectSelectedItems().forEach(({ item }) => {
    const variantId = String(item.id).replace(/\D/g, "");
    if (!variantId || item.available === false) {
      excluded.push(item.title);
      return;
    }
    cartLines.push(`${variantId}:1`);
  });

  if (excluded.length > 0) {
    console.warn("[Cart URL] Excluded unavailable items:", excluded);
  }

  if (cartLines.length === 0) {
    return `${window.location.origin}/cart`;
  }

  return `${window.location.origin}/cart/${cartLines.join(",")}`;
}

/**
 * Shows an inline error message above the review grid.
 * @param {string} message - Error text to display
 * @returns {void}
 */
function showCartError(message) {
  const contentArea = rfElements.contentArea;
  if (!contentArea) return;

  let errorBox = contentArea.querySelector(".rf-error-box");
  if (!errorBox) {
    errorBox = document.createElement("div");
    errorBox.className = "rf-error-box";
    errorBox.style.cssText =
      "text-align:center;padding:1rem;margin-bottom:1rem;color:#de1414;";
    contentArea.prepend(errorBox);
  }
  errorBox.textContent = message;
}

/**
 * Adds the whole build to the Shopify cart via the Ajax Cart API.
 *
 * One batch POST, deliberately all-or-nothing: a partial rig in the cart is
 * worse than a clean failure. The cart is appended to — a shopper's existing
 * items are never touched.
 *
 * Bundle contract (read by the cart-transform function in the
 * simhq-rig-bundles app): every line carries a hidden `_rig_bundle_id`
 * property, and the first line is flagged `_rig_parent`. The function merges
 * lines sharing a bundle id under the parent's variant as one
 * "SIM HQ Custom Rig" line, so removing that line removes the whole rig.
 * Until the app is deployed and activated, the properties are inert.
 * @returns {Promise<void>}
 */
async function addAllToCart() {
  const bundleId =
    (window.crypto && crypto.randomUUID && crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const items = [];

  collectSelectedItems().forEach(({ item }) => {
    if (item.available === false) return;
    const variantId = String(item.id).replace(/\D/g, "");
    if (!variantId) return;

    const properties = { _rig_bundle_id: bundleId };
    if (items.length === 0) {
      properties._rig_parent = "true";
    }
    items.push({ id: parseInt(variantId, 10), quantity: 1, properties });
  });

  if (items.length === 0) {
    alert("No available items to add. Check marked items on the review page.");
    return;
  }

  const btn = rfElements.nextBtn;
  const originalText = btn.textContent;
  btn.textContent = "Adding...";
  btn.disabled = true;

  try {
    const response = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items }),
    });

    if (response.ok) {
      btn.textContent = `Added ${items.length} item${items.length > 1 ? "s" : ""}!`;
      btn.style.background = "var(--rf-success)";
      openCartDrawer();
    } else {
      const data = await response.json().catch(() => ({}));
      const reason = data.description || data.message || `HTTP ${response.status}`;
      console.error("[Cart] Batch failed:", reason);
      showCartError(`Could not add your build to the cart: ${reason}`);
      btn.textContent = "Failed — retry";
      btn.style.background = "var(--rf-error)";
    }
  } catch (error) {
    console.error("[Cart] Network error:", error.message);
    showCartError("Could not add your build to the cart. Please check your connection and try again.");
    btn.textContent = "Failed — retry";
    btn.style.background = "var(--rf-error)";
  }

  setTimeout(() => {
    btn.textContent = originalText;
    btn.style.background = "";
    btn.disabled = false;
  }, 3000);
}

/**
 * Opens the theme's cart drawer with fresh content.
 * Fetches updated section HTML and uses the CartDrawer custom element's
 * renderContents method; falls back to clicking the cart icon, then /cart.
 * @returns {Promise<void>}
 */
async function openCartDrawer() {
  const cartDrawer = document.querySelector("cart-drawer");

  if (cartDrawer && typeof cartDrawer.renderContents === "function") {
    // Fetch fresh cart data with section rendering
    try {
      const response = await fetch(
        "/cart.js?sections=cart-drawer,cart-icon-bubble"
      );
      const cartData = await response.json();

      // renderContents expects { sections: { ... } } and opens the drawer
      cartDrawer.renderContents({
        sections: {
          "cart-drawer": cartData["cart-drawer"],
          "cart-icon-bubble": cartData["cart-icon-bubble"],
        },
      });
      return;
    } catch (error) {
      console.warn("[Cart] Could not refresh cart drawer:", error.message);
    }
  }

  // Fallback: click the cart icon to trigger native drawer open
  const cartIcon = document.getElementById("cart-icon-bubble");
  if (cartIcon) {
    setTimeout(() => cartIcon.click(), 300);
  } else {
    setTimeout(() => {
      window.location.href = "/cart";
    }, 500);
  }
}

/**
 * Builds a configuration URL for sharing the current build.
 * Format: s0:id1,s2:id2,k3 (s=selection with index:id, k=skip with index)
 * Validates URL length stays under 2000 chars for browser compatibility.
 * @returns {string} URL with config parameter
 */
function buildConfigUrl() {
  const url = new URL(window.location.origin + window.location.pathname);
  const stateParts = [];

  // Encode selections (s + index : id OR s + index : id1|id2|id3 for multi-select)
  Object.entries(appState.selections).forEach(([index, item]) => {
    if (Array.isArray(item)) {
      const ids = item.map((i) => i.id).join("|");
      if (ids) stateParts.push(`s${index}:${ids}`);
    } else if (item && item.id) {
      stateParts.push(`s${index}:${item.id}`);
    }
  });

  // Encode skips (k + index)
  Object.keys(appState.skippedSteps).forEach((index) => {
    stateParts.push(`k${index}`);
  });

  if (stateParts.length > 0) {
    url.searchParams.set("config", stateParts.join(","));
  }

  const finalUrl = url.toString() + "#rig-flow-container";

  // Warn if URL exceeds safe browser limit
  if (finalUrl.length > 2000) {
    console.warn(
      `[Config URL] URL length (${finalUrl.length}) exceeds 2000 char limit. Some browsers may truncate it.`,
    );
  }

  return finalUrl;
}

/**
 * Rebuilds app state from URL configuration parameter.
 * Handles missing products, sets furthestStep, and cleans URL after rebuild.
 * @param {string} configString - Encoded config string from URL
 * @returns {Promise<boolean>} True if successful
 */
async function rebuildConfigFromUrl(configString) {
  if (!configString) return false;

  try {
    const parts = configString.split(",");
    const allIds = []; // Collect all unique IDs
    const selectionMapByStep = {}; // Map: stepIndex -> [id1, id2, ...]

    parts.forEach((part) => {
      if (part.startsWith("s")) {
        // Selection: s0:12345 OR s0:12345|67890 for multi-select
        const [indexStr, idsStr] = part.slice(1).split(":");
        const index = parseInt(indexStr, 10);
        if (!isNaN(index) && idsStr) {
          const ids = idsStr.split("|");
          selectionMapByStep[index] = ids;
          allIds.push(...ids);
        }
      } else if (part.startsWith("k")) {
        // Skip: k1
        const index = parseInt(part.slice(1), 10);
        if (!isNaN(index)) {
          appState.skippedSteps[index] = true;
        }
      }
    });

    // Fetch all products, passing the full catalog metadata so rebuilt
    // selections keep their nextStepData (branch filtering)
    if (allIds.length > 0) {
      const items = await fetchProductsGraphQL(
        allIds,
        collectAllConfigObjects(),
      );
      const itemsById = {};
      items.forEach((item) => {
        // Placeholders are unresolvable products — treat as missing
        if (item && !item._isPlaceholder) itemsById[item.id] = item;
      });

      // Rebuild appState.selections
      const unavailable = [];
      Object.entries(selectionMapByStep).forEach(([stepIndex, ids]) => {
        const step = partsData.steps[stepIndex];
        if (!step) return;

        const matchedItems = ids.map((id) => itemsById[id]).filter(Boolean);
        const missingIds = ids.filter((id) => !itemsById[id]);

        if (missingIds.length > 0) {
          unavailable.push(
            `Step ${parseInt(stepIndex) + 1} (${step.name}): ${missingIds.length} item(s) no longer available`,
          );
        }

        // Never store null/empty selections — a missing product must leave
        // the step unselected, not crash summary/render code downstream
        if (matchedItems.length === 0) {
          delete appState.selections[stepIndex];
        } else if (step.allowMultiple) {
          appState.selections[stepIndex] = matchedItems;
        } else {
          appState.selections[stepIndex] = matchedItems[0];
        }
      });

      if (unavailable.length > 0) {
        console.warn("[Config Rebuild] Some items unavailable:", unavailable);
      }
    }

    // Set furthestStep to the highest configured step + 1
    let maxStep = 0;
    Object.keys(selectionMapByStep).forEach((idx) => {
      const i = parseInt(idx, 10);
      if (i >= maxStep) maxStep = i + 1;
    });
    Object.keys(appState.skippedSteps).forEach((idx) => {
      const i = parseInt(idx, 10);
      if (i >= maxStep) maxStep = i + 1;
    });
    appState.furthestStep = Math.min(maxStep, appState.totalSteps);

    // Clear config param from URL to prevent stale reloads
    const cleanUrl = new URL(window.location.href);
    cleanUrl.searchParams.delete("config");
    window.history.replaceState({}, "", cleanUrl.toString());

    return true;
  } catch (error) {
    console.error("[Config] Failed to rebuild from URL:", error);
    return false;
  }
}

// ============================================================================
// SECTION 8: PERSISTENCE
// ============================================================================

/**
 * Saves current progress to sessionStorage.
 * @returns {void}
 */
function saveProgress() {
  try {
    const data = {
      selections: appState.selections,
      skipped: appState.skippedSteps,
      current: appState.currentStep,
      furthest: appState.furthestStep,
    };
    sessionStorage.setItem("rf_build_state", JSON.stringify(data));
  } catch (error) {
    console.warn("[Save] sessionStorage unavailable:", error.message);
  }
}

/**
 * Loads saved progress from sessionStorage.
 * @returns {void}
 */
function loadSavedProgress() {
  const saved = sessionStorage.getItem("rf_build_state");
  if (!saved) return;

  try {
    const data = JSON.parse(saved);
    appState.selections = data.selections || {};
    appState.skippedSteps = data.skipped || {};
    appState.currentStep = data.current || 0;
    appState.furthestStep = data.furthest || 0;
  } catch (error) {
    console.warn("Failed to load saved build", error);
  }
}

// ============================================================================
// SECTION 9: INITIALIZATION & EVENT HANDLERS
// ============================================================================

/**
 * Initializes references to DOM elements.
 * @returns {void}
 */
function initComponentElements() {
  rfElements = {
    navList: document.getElementById("rf-nav-list"),
    contentArea: document.getElementById("rf-content-area"),
    prevBtn: document.getElementById("rf-prev-btn"),
    nextBtn: document.getElementById("rf-next-btn"),
    skipBtn: document.getElementById("rf-skip-btn"),
    shareBtn: document.getElementById("rf-share-btn"),
    checkoutLinkBtn: document.getElementById("rf-checkout-link-btn"),
    footerShareBtn: document.getElementById("rf-footer-share-btn"),
    footerResetBtn: document.getElementById("rf-footer-reset-btn"),
    resetBtn: document.getElementById("rf-reset-btn"),
    totalDisplay: document.getElementById("cart-total-display"),
    footerTotalDisplay: document.getElementById("footer-cart-total-display"),
    jumpReviewBtn: document.getElementById("rf-jump-review-btn"),
    stepCounter: document.getElementById("rf-progress-counter"),
    pipsContainer: document.getElementById("rf-progress-pips"),
    drawerItems: document.getElementById("rf-drawer-items"),
    drawerTab: document.getElementById("rf-drawer-tab"),
    buildTotalDisplay: document.getElementById("build-total-display"),
    appContainer: document.getElementById("rig-flow-container"),
  };
}

/**
 * Main initialization function.
 * Sets up the configurator, loads data, and renders initial state.
 * @returns {Promise<void>}
 */
async function init() {
  if (!document.getElementById("rf-content-area")) return;

  initComponentElements();
  attachButtonHandlersOnce();

  // Desktop build drawer starts closed unless the shopper left it open
  setBuildDrawerOpen(sessionStorage.getItem(DRAWER_STATE_KEY) === "1");

  // Set up jump to review button
  if (rfElements.jumpReviewBtn) {
    rfElements.jumpReviewBtn.onclick = () => {
      appState.isReviewStep = true;
      updateSidebarVisuals();
      renderCurrentStep();
    };
  }

  // Set up reset button
  if (rfElements.resetBtn) {
    rfElements.resetBtn.onclick = resetConfigurator;
  }

  // Check for shared config URL param (rebuild happens after data loads)
  const urlParams = new URLSearchParams(window.location.search);
  const sharedConfig = urlParams.get("config");

  if (!sharedConfig) {
    loadSavedProgress();
  }

  try {
    // Surface data-file typos: branching resolves by step display name
    validateBranchKeys();

    // Initialize product data
    const productConfig = buildProductConfig(configuratorData);
    partsData = await loadProductDataByIds(productConfig);
    appState.totalSteps = partsData.steps.length;

    // Rebuild from shared config URL (must happen after partsData is loaded)
    if (sharedConfig) {
      const success = await rebuildConfigFromUrl(sharedConfig);
      if (success) {
        sessionStorage.removeItem("rf_build_state");
      }
    }

    // Determine starting step
    let startStep = 0;
    let isBuildFinished = true;

    for (let i = 0; i < appState.totalSteps; i++) {
      if (!appState.selections[i] && !appState.skippedSteps[i]) {
        startStep = i;
        isBuildFinished = false;
        break;
      }
    }

    if (isBuildFinished) {
      appState.isReviewStep = true;
    } else {
      appState.currentStep = startStep;
      appState.isReviewStep = false;
    }

    // Render UI
    initializeSidebarNav();
    updateSidebarVisuals();
    renderCurrentStep();
    updateCartSummary();
  } catch (error) {
    console.error("[Init] Error during initialization:", error);
    if (document.getElementById("rf-content-area")) {
      document.getElementById("rf-content-area").innerHTML =
        `<div class="rf-error-box">Initialization Failed: ${escapeHTML(error.message)}</div>`;
    }
  } finally {
    const loader = document.getElementById("rf-loader");
    if (loader) loader.style.display = "none";
  }
}

// Start initialization on DOM ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
